import type {
    MediaResponse,
    PaginatedMediaResponse,
    UploadMediaRequest,
    UpdateAttachmentRequest,
    DeleteMediaResponse,
    MediaFilters
} from '@/types/media';
import apiClient from './meta-client';

const META_API_ORIGIN = (import.meta.env.VITE_META_API_URL || 'http://localhost:8000/api/v1')
    .replace(/\/api\/v\d+.*$/, '');

const normalizeFileUrl = (media: MediaResponse): MediaResponse => {
    if (!media.file_url) return media;
    if (media.file_url.startsWith('http')) return media;
    // file_url sem domínio ex: "/images/foo.png" → falta também o /uploads
    const path = media.file_url.startsWith('/uploads') ? media.file_url : `/uploads${media.file_url}`;
    return { ...media, file_url: `${META_API_ORIGIN}${path}` };
};

// ========== MEDIA API ==========

/**
 * Listar todas as mídias do usuário
 * @param filters - Filtros opcionais (media_type, page_id, page, per_page)
 * @returns Lista de mídias
 */
export const getMedias = async (filters?: MediaFilters): Promise<MediaResponse[]> => {
    const params: Record<string, string> = {};

    if (filters?.media_type) {
        params.media_type = filters.media_type;
    }

    if (filters?.page_id) {
        params.page_id = filters.page_id;
    }

    if (filters?.page) {
        params.page = String(filters.page);
    }

    if (filters?.per_page) {
        params.per_page = String(filters.per_page);
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<PaginatedMediaResponse>(
        `/media${queryString ? `?${queryString}` : ''}`
    );

    // Retorna apenas os items do objeto paginado
    return response.data.items.map(normalizeFileUrl);
};

/**
 * Listar mídias com informações de paginação
 * @param filters - Filtros opcionais (media_type, page_id, page, per_page)
 * @returns Resposta paginada completa
 */
export const getMediasPaginated = async (filters?: MediaFilters): Promise<PaginatedMediaResponse> => {
    const params: Record<string, string> = {};

    if (filters?.media_type) {
        params.media_type = filters.media_type;
    }

    if (filters?.page_id) {
        params.page_id = filters.page_id;
    }

    if (filters?.page) {
        params.page = String(filters.page);
    }

    if (filters?.per_page) {
        params.per_page = String(filters.per_page);
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<PaginatedMediaResponse>(
        `/media${queryString ? `?${queryString}` : ''}`
    );

    return {
        ...response.data,
        items: response.data.items.map(normalizeFileUrl),
    };
};

/**
 * Obter mídia específica por ID
 * @param mediaId - ID da mídia
 * @returns Dados da mídia
 */
export const getMedia = async (mediaId: number): Promise<MediaResponse> => {
    const response = await apiClient.get<MediaResponse>(`/media/${mediaId}`);
    return normalizeFileUrl(response.data);
};

/**
 * Upload de nova mídia
 * @param data - Dados do upload (file, page_id, send_to_meta)
 * @returns Mídia criada
 */
export const uploadMedia = async (data: UploadMediaRequest): Promise<MediaResponse> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('page_id', data.page_id);

    // Se não especificado, envia para Meta apenas se for vídeo ou áudio
    const sendToMeta = data.send_to_meta ?? (
        data.file.type.startsWith('video/') || data.file.type.startsWith('audio/')
    );
    formData.append('send_to_meta', String(sendToMeta));

    const response = await apiClient.post<MediaResponse>('/media/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return normalizeFileUrl(response.data);
};

/**
 * Atualizar attachment_id de uma mídia (fallback manual)
 * @param mediaId - ID da mídia
 * @param data - Novo attachment_id
 * @returns Mídia atualizada
 */
export const updateMediaAttachment = async (
    mediaId: number,
    data: UpdateAttachmentRequest
): Promise<MediaResponse> => {
    const response = await apiClient.put<MediaResponse>(
        `/media/${mediaId}/attachment`,
        data
    );
    return response.data;
};

/**
 * Deletar mídia
 * @param mediaId - ID da mídia
 * @returns Resposta de sucesso
 */
export const deleteMedia = async (mediaId: number): Promise<DeleteMediaResponse> => {
    const response = await apiClient.delete<DeleteMediaResponse>(`/media/${mediaId}`);
    return response.data;
};

// ========== HELPER FUNCTIONS ==========

/**
 * Valida tipo de arquivo baseado no mediaType
 * @param file - Arquivo a validar
 * @param mediaType - Tipo de mídia esperado
 * @returns true se válido
 */
export const validateMediaType = (
    file: File,
    mediaType: 'image' | 'video' | 'audio' | 'all'
): boolean => {
    const validTypes: Record<string, string[]> = {
        image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        video: ['video/mp4', 'video/quicktime'],
        audio: ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav'],
        all: [
            'image/jpeg', 'image/png', 'image/gif', 'image/webp',
            'video/mp4', 'video/quicktime',
            'audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav'
        ]
    };

    return validTypes[mediaType].includes(file.type);
};

/**
 * Valida tamanho do arquivo
 * @param file - Arquivo a validar
 * @param maxSizeMB - Tamanho máximo em MB (padrão: 25MB)
 * @returns true se válido
 */
export const validateFileSize = (file: File, maxSizeMB: number = 25): boolean => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxBytes;
};

/**
 * Formata tamanho de arquivo para exibição
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "2.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Obtém ícone/label baseado no tipo de mídia
 * @param mediaType - Tipo da mídia
 * @returns Label formatada
 */
export const getMediaTypeLabel = (mediaType: string): string => {
    const labels: Record<string, string> = {
        image: 'Imagem',
        video: 'Vídeo',
        audio: 'Áudio'
    };
    return labels[mediaType] || mediaType;
};
